import { useEffect, useState, useCallback } from "react";
import { Droplets, Clock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { inputCls, Label } from "./shared";

export function HotSpringsPanel() {
  const [ev, setEv] = useState(null);
  const [hours, setHours] = useState(72);
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(Date.now());

  const load = useCallback(() => api.get("/admin/hot-springs").then((r) => setEv(r.data)).catch(() => setEv({ active: false })), []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const id = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(id); }, []);

  const start = async () => {
    if (!(hours > 0)) return toast.error("Enter a duration greater than 0");
    setBusy(true);
    try { const r = await api.post("/admin/hot-springs", { hours: Number(hours) }); setEv(r.data); toast.success("The Hot Springs are open!"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  const stop = async () => {
    setBusy(true);
    try { const r = await api.post("/admin/hot-springs", { hours: 0 }); setEv(r.data); toast.success("The Hot Springs have closed"); }
    catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  let remaining = "";
  if (ev?.active && ev.active_until) {
    const ms = new Date(ev.active_until).getTime() - now;
    if (ms > 0) {
      const h = Math.floor(ms / 3600000), m = Math.floor((ms % 3600000) / 60000), s = Math.floor((ms % 60000) / 1000);
      remaining = `${h}h ${m}m ${s}s`;
    }
  }

  return (
    <div data-testid="hot-springs-panel" className={`mb-6 border p-4 rounded-sm ${ev?.active ? "border-cyan-500/60 bg-cyan-950/20" : "border-cyan-900/30 bg-zinc-950"}`}>
      <div className="flex items-center gap-2 mb-3">
        <Droplets size={16} className="text-cyan-400" />
        <h3 className="font-cinzel text-cyan-300 uppercase tracking-widest text-sm">The Hot Springs</h3>
        {ev?.active && (
          <span data-testid="hot-springs-active-badge" className="ml-auto flex items-center gap-1.5 text-xs text-cyan-300 font-semibold">
            <Clock size={13} /> -{ev.reduction_pct}% recovery · {remaining}
          </span>
        )}
      </div>
      {ev?.active ? (
        <div className="flex flex-wrap items-center gap-3">
          <p className="text-sm text-zinc-400 flex-1 min-w-[200px]">Every gladiator's arena recovery is <span className="text-cyan-300 font-semibold">{ev.reduction_pct}% faster</span> (stacks with VIP) until the timer ends.</p>
          <button data-testid="hot-springs-stop-btn" onClick={stop} disabled={busy}
            className="px-4 py-2 border border-red-900/50 text-red-300 hover:border-red-500 text-sm rounded-sm disabled:opacity-50">Close Springs Now</button>
        </div>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <p className="text-sm text-zinc-500 w-full mb-1">Open the bathhouse to cut gladiator recovery cooldowns by 30% platform-wide for a limited time. Stacks with VIP.</p>
          <div className="w-28"><Label>Duration (hours)</Label>
            <input data-testid="hot-springs-hours" type="number" min="1" step="1" className={inputCls} value={hours} onChange={(e) => setHours(e.target.value)} /></div>
          <button data-testid="hot-springs-start-btn" onClick={start} disabled={busy}
            className="px-5 py-2 bg-cyan-500 text-zinc-950 font-cinzel uppercase tracking-wide text-sm hover:bg-cyan-400 disabled:opacity-50 rounded-sm">Open Springs</button>
        </div>
      )}
    </div>
  );
}
